import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusPill, EnvBadge } from "@/components/StatusPill";
import { useI18n } from "@/i18n/I18nContext";
import { ArrowLeft, Loader2, Users, ListChecks, GitCommitHorizontal, ShieldCheck, Check, X, RotateCcw, FileText, Download, Rocket, CalendarDays, Target, Layers, Radio, UserCheck, Image as ImageIcon, Wrench, Route, PackageCheck } from "lucide-react";
import { toast } from "sonner";

const ROLE_COLOR = { LEAD: "#2563EB", SPECIALIST: "#38BDF8", REVIEWER: "#FBBF24", ADVISOR: "#A5B4FC" };
const CAL_HEADERS = ["md.thDay", "md.thTopic", "md.thFormat", "md.thHook", "md.thKeyMessage", "md.thCta", "md.thProdNotes", "md.thRole"];

function Section({ icon: Icon, title, children, testid, accent = "#2563EB" }) {
  return (
    <div className="m-card p-5" data-testid={testid}>
      <p className="mono-label mb-4 flex items-center gap-2"><Icon size={14} style={{ color: accent }} /> {title}</p>
      {children}
    </div>
  );
}

function List({ items }) {
  return <ul className="space-y-1.5">{(items || []).map((x, i) => <li key={i} className="text-sm text-[#CBD5E1] flex gap-2"><span className="text-[#2563EB]">•</span>{x}</li>)}</ul>;
}

function TaskMeta({ task }) {
  const cp = task.checkpoint;
  return (
    <div className="mt-2 space-y-2">
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] font-mono text-[#64748B]">
        {task.execution_tool && <span>TOOL {task.execution_tool}</span>}
        {task.operating_module && <span>MODULE {task.operating_module}</span>}
        {task.playbook && <span>PLAYBOOK {task.playbook}</span>}
        {task.qa_state && <span>QA {task.qa_state}</span>}
        {task.branch && <span>GIT {task.branch}</span>}
        {task.commit && <span>COMMIT {String(task.commit).slice(0, 10)}</span>}
        {task.pull_request && <span>PR {task.pull_request}</span>}
      </div>
      {task.blocker && <p className="text-[11px] text-[#FCA5A5]">BLOCKER · {task.blocker}</p>}
      {task.next_action && <p className="text-[11px] text-[#94A3B8]">NEXT · {task.next_action}</p>}
      {cp && (
        <div className="rounded-lg border border-[#4338CA] bg-[#1E1B4B]/30 p-2.5 text-[11px]" data-testid={`mission-task-checkpoint-${task.id}`}>
          <p className="font-mono text-[#A5B4FC]">CHECKPOINT · {cp.cause} · {(cp.created_at || "").slice(0, 16).replace("T", " ")}</p>
          {(cp.work_completed || []).length > 0 && <p className="text-[#CBD5E1] mt-1">WORK COMPLETED · {cp.work_completed.join(" · ")}</p>}
          {(cp.files_changed || []).length > 0 && <p className="text-[#94A3B8] mt-1">FILES · {cp.files_changed.join(", ")}</p>}
          {cp.test_state && <p className="text-[#94A3B8] mt-1">TEST · {cp.test_state}</p>}
          {cp.unresolved_issue && <p className="text-[#FCA5A5] mt-1">UNRESOLVED · {cp.unresolved_issue}</p>}
          {cp.resume_point && <p className="text-[#E2E8F0] mt-1">RESUME POINT · {cp.resume_point}</p>}
          {cp.next_action && <p className="text-[#E2E8F0] mt-1">NEXT · {cp.next_action}</p>}
        </div>
      )}
      {task.execution_handoff && (
        <div className="rounded-lg border border-[#0E7490] bg-[#083344]/30 p-2.5 text-[11px]" data-testid={`mission-task-handoff-${task.id}`}>
          <p className="font-mono text-[#67E8F9] flex items-center gap-1.5">
            <PackageCheck size={12} /> EXECUTION HANDOFF · {task.execution_handoff.status}
          </p>
          {task.execution_handoff.target_repo && <p className="text-[#CBD5E1] mt-1">REPO · {task.execution_handoff.target_repo}</p>}
          {task.execution_handoff.suggested_branch && <p className="text-[#94A3B8] mt-1">SUGGESTED BRANCH · {task.execution_handoff.suggested_branch}</p>}
          {task.execution_handoff.actual_branch && <p className="text-[#94A3B8] mt-1">ACTUAL BRANCH · {task.execution_handoff.actual_branch}</p>}
          {task.execution_handoff.actual_commit && <p className="text-[#94A3B8] mt-1">COMMIT · {task.execution_handoff.actual_commit}</p>}
          {task.execution_handoff.actual_pull_request && <p className="text-[#94A3B8] mt-1">PR · {task.execution_handoff.actual_pull_request}</p>}
          {task.execution_handoff.test_state && <p className="text-[#94A3B8] mt-1">TEST · {task.execution_handoff.test_state}</p>}
          <p className="text-[10px] text-[#64748B] mt-1">TRACE · {task.execution_handoff.trace_verification}</p>
        </div>
      )}
    </div>
  );
}

export default function MissionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.mission(id).then(setM), [id]);
  useEffect(() => { load(); }, [load]);

  const ACTION_LABEL = { APPROVE: t("md.actionApprove"), REVISE: t("md.actionRevise"), REJECT: t("md.actionReject") };

  const decide = async (aid, action) => {
    setBusy(true);
    try {
      await api.decideApproval(aid, action, t("md.notePrefix", { action }));
      toast.success(t("md.actionToast", { v: ACTION_LABEL[action] || action }));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || t("common.actionFailed")); } finally { setBusy(false); }
  };

  const exportDeliverable = () => {
    const md = m.deliverable_markdown || t("md.exportEmpty");
    const blob = new Blob([md], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${m.code}-final-deliverable.md`; a.click();
    URL.revokeObjectURL(url);
    toast.success(t("md.exported"));
  };

  const createProduction = async () => {
    setBusy(true);
    try {
      const { job_id } = await api.createProductionMission(id);
      toast.info(t("md.creatingProd"));
      const poll = setInterval(async () => {
        const job = await api.commandJob(job_id);
        if (job.status === "COMPLETED") {
          clearInterval(poll);
          setBusy(false);
          const nid = job.result?.mission?.id;
          toast.success(t("md.prodCreated"));
          if (nid) navigate(`/missions/${nid}`);
        } else if (job.status === "FAILED") {
          clearInterval(poll);
          setBusy(false);
          toast.error(t("md.prodFailed"));
        }
      }, 4000);
    } catch (e) { toast.error(e.response?.data?.detail || t("common.failed")); setBusy(false); }
  };

  if (!m) return <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-[#2563EB]" /></div>;
  const pending = (m.approvals || []).find((a) => a.status === "PENDING");
  const approved = m.status === "COMPLETED";
  const fd = m.final_deliverable;
  const reviews = (m.tasks || []).filter((tk) => tk.type === "REVIEW");
  const agentTasks = (m.tasks || []).filter((tk) => tk.type === "AGENT_TASK");
  const compileTask = (m.tasks || []).find((tk) => tk.type === "COMPILE");

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/missions")} className="text-sm text-[#94A3B8] hover:text-white flex items-center gap-1.5" data-testid="mission-back"><ArrowLeft size={15} /> {t("md.back")}</button>

      {/* Objective header */}
      <div className="m-card p-6" style={{ background: "linear-gradient(135deg,#0F1626,#101d33)" }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="mono-label">{m.code} · {m.project_code} · workflow {m.workflow}{m.mission_kind === "PRODUCTION" ? " · PRODUCTION" : ""}</p>
            <h1 className="font-display text-2xl font-extrabold text-white mt-1">{m.title}</h1>
            <p className="text-sm text-[#94A3B8] mt-2 max-w-2xl" data-testid="mission-objective">{m.objective}</p>
          </div>
          <div className="text-right flex flex-col items-end gap-2"><div className="flex items-center gap-2"><EnvBadge environment={m.environment} archived={m.archived} /><StatusPill status={m.status} /></div><p className="text-xs text-[#64748B] font-mono">cost ${m.cost}</p></div>
        </div>
        <p className="text-xs text-[#64748B] mt-4 border-t border-[#1E293B] pt-3"><span className="text-[#94A3B8]">{t("md.successCriteria")}</span> {m.success_criteria}</p>
      </div>

      {m.routing_plan && (
        <div className="m-card p-4" data-testid="mission-routing-plan">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="mono-label flex items-center gap-2"><Route size={14} className="text-[#38BDF8]" /> ROUTING LOGIC V1</p>
            <span className="text-[9px] text-[#64748B] font-mono">{m.routing_plan.execution_binding}</span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[10px] font-mono text-[#64748B]">
            <span>MODULE <span className="text-[#94A3B8]">{m.routing_plan.operating_module}</span></span>
            <span>PLAYBOOK <span className="text-[#94A3B8]">{m.routing_plan.playbook || "—"}</span></span>
            <span>WORK TYPE <span className="text-[#94A3B8]">{m.routing_plan.work_type}</span></span>
            <span>FUNCTIONAL OWNER <span className="text-[#94A3B8]">{m.routing_plan.functional_owner}</span></span>
            <span>TOOL <span className="text-[#94A3B8]">{m.routing_plan.selected_execution_tool}</span></span>
          </div>
          {m.routing_plan.approval_boundaries?.length > 0 && (
            <p className="text-[11px] text-[#FCA5A5] mt-2">
              APPROVAL BOUNDARY · {m.routing_plan.approval_boundaries.join(" · ")}
            </p>
          )}
          {m.routing_plan.unresolved?.length > 0 && (
            <p className="text-[11px] text-[#FBBF24] mt-2">
              UNRESOLVED · {m.routing_plan.unresolved.join(" · ")}
            </p>
          )}
        </div>
      )}

      {/* Founder approval + actions */}
      {pending ? (
        <div className="m-card p-5" style={{ borderColor: "#BE123C" }} data-testid="mission-approval-block">
          <div className="flex items-center gap-2 mb-3"><ShieldCheck size={17} className="text-[#FB7185]" /><p className="font-display font-bold text-white">{t("md.approvalRequired")}</p><StatusPill status={pending.class} /></div>
          <p className="text-sm text-[#E2E8F0]">{pending.reason}</p>
          {pending.has_fallback && <div className="mt-3 flex items-start gap-2 text-xs text-[#FDBA74] bg-[#431407] border border-[#EA580C] rounded-lg p-3"><X size={14} className="mt-0.5 shrink-0" />{t("md.fallbackWarning")}</div>}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-xs">
            {[[t("md.risk"), pending.risk], [t("md.cost"), `$${pending.cost}`], [t("md.reversibility"), pending.reversibility], [t("md.aiRec"), pending.ai_recommendation]].map(([k, v]) => (
              <div key={k} className="bg-[#0B0F19] rounded-lg p-3 border border-[#1E293B]"><p className="mono-label">{k}</p><p className="text-white mt-1">{v}</p></div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <button disabled={busy || pending.has_fallback} title={pending.has_fallback ? t("common.retryProviderTitle") : ""} onClick={() => decide(pending.id, "APPROVE")} className="btn-primary h-10 px-5 text-sm flex items-center gap-2 disabled:opacity-40" data-testid="mission-approve-button"><Check size={15} /> {t("common.approve")}</button>
            <button disabled={busy} onClick={() => decide(pending.id, "REVISE")} className="btn-ghost h-10 px-5 text-sm flex items-center gap-2" data-testid="mission-revise-button"><RotateCcw size={15} /> {t("md.requestRevision")}</button>
            <button disabled={busy} onClick={exportDeliverable} className="btn-ghost h-10 px-5 text-sm flex items-center gap-2" data-testid="mission-export-button-pending"><Download size={15} /> {t("common.export")}</button>
            <button disabled={busy} onClick={() => decide(pending.id, "REJECT")} className="btn-ghost h-10 px-5 text-sm flex items-center gap-2" style={{ color: "#FCA5A5" }} data-testid="mission-reject-button"><X size={15} /> {t("common.reject")}</button>
          </div>
        </div>
      ) : (
        <div className="m-card p-4 flex flex-wrap items-center gap-2" data-testid="mission-actions">
          {approved && <span className="text-xs text-[#34D399] flex items-center gap-1.5 mr-2"><Check size={14} /> {t("md.founderApproved")}</span>}
          <button disabled={busy} onClick={exportDeliverable} className="btn-ghost h-9 px-4 text-sm flex items-center gap-2" data-testid="mission-export-button"><Download size={15} /> {t("common.export")}</button>
          <button disabled={busy || !approved} title={approved ? "" : t("md.approveFirstTitle")} onClick={createProduction} className="btn-primary h-9 px-4 text-sm flex items-center gap-2 disabled:opacity-40" data-testid="mission-create-production-button"><Rocket size={15} /> {t("md.createProduction")}</button>
        </div>
      )}

      {/* Compiled Final Deliverable */}
      <Section icon={FileText} title={t("md.finalDeliverable")} testid="mission-final-deliverable" accent="#34D399">
        {!m.deliverable_markdown && <p className="text-sm text-[#64748B]">{t("md.noDeliverable")}</p>}
        {fd && m.deliverable_type === "CONTENT_PLAN" ? (
          <div className="space-y-5">
            <div className="grid md:grid-cols-2 gap-4">
              <div><p className="text-xs text-[#64748B] flex items-center gap-1.5 mb-1"><Target size={12} /> {t("md.strategy")}</p><p className="text-sm text-[#E2E8F0]">{fd.strategy_summary}</p></div>
              <div><p className="text-xs text-[#64748B] flex items-center gap-1.5 mb-1"><Users size={12} /> {t("md.audience")}</p><p className="text-sm text-[#E2E8F0]">{fd.target_audience}</p></div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div><p className="text-xs text-[#64748B] mb-1">{t("md.objectives")}</p><List items={fd.content_objectives} /></div>
              <div><p className="text-xs text-[#64748B] flex items-center gap-1.5 mb-1"><Layers size={12} /> {t("md.pillars")}</p><List items={fd.content_pillars} /></div>
              <div><p className="text-xs text-[#64748B] flex items-center gap-1.5 mb-1"><Radio size={12} /> {t("md.channelMix")}</p><List items={fd.channel_mix} /></div>
            </div>
            <div>
              <p className="text-xs text-[#64748B] flex items-center gap-1.5 mb-2"><CalendarDays size={12} /> {t("md.calendar", { days: m.deliverable_days })}</p>
              <div className="overflow-x-auto rounded-xl border border-[#1E293B]">
                <table className="w-full text-xs" data-testid="content-calendar-table">
                  <thead><tr className="bg-[#0B0F19] text-[#64748B] text-left">
                    {CAL_HEADERS.map((h) => <th key={h} className="p-2.5 font-medium whitespace-nowrap">{t(h)}</th>)}
                  </tr></thead>
                  <tbody>
                    {(fd.calendar || []).map((c, i) => (
                      <tr key={i} className="border-t border-[#1E293B] align-top" data-testid={`calendar-day-${c.day}`}>
                        <td className="p-2.5 font-mono text-[#2563EB]">{c.day}</td>
                        <td className="p-2.5 text-white min-w-[130px]">{c.topic}</td>
                        <td className="p-2.5 text-[#94A3B8] whitespace-nowrap">{c.format}</td>
                        <td className="p-2.5 text-[#CBD5E1] min-w-[140px]">{c.hook}</td>
                        <td className="p-2.5 text-[#CBD5E1] min-w-[140px]">{c.key_message}</td>
                        <td className="p-2.5 text-[#94A3B8] min-w-[90px]">{c.cta}</td>
                        <td className="p-2.5 text-[#94A3B8] min-w-[140px]">{c.production_notes}</td>
                        <td className="p-2.5 text-[#A5B4FC] whitespace-nowrap">{c.responsible_role}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div><p className="text-xs text-[#64748B] flex items-center gap-1.5 mb-1"><ShieldCheck size={12} /> {t("md.complianceNotes")}</p><p className="text-sm text-[#E2E8F0]">{fd.brand_compliance_notes}</p></div>
          </div>
        ) : (
          m.deliverable_markdown && <div className="text-sm text-[#CBD5E1] whitespace-pre-wrap max-h-[600px] overflow-y-auto leading-relaxed">{m.deliverable_markdown}</div>
        )}
      </Section>

      {/* SPEC-06: Generated Assets (real tool output, media visible) */}
      {(m.media_assets || []).length > 0 && (
        <Section icon={ImageIcon} title={t("md.generatedAssets")} testid="mission-generated-assets" accent="#38BDF8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {m.media_assets.map((a) => (
              <div key={a.id} className="bg-[#0B0F19] border border-[#1E293B] rounded-xl overflow-hidden" data-testid={`generated-asset-${a.id}`}>
                {a.media_data_url && a.asset_type === "image" && (
                  <img src={a.media_data_url} alt={a.name} className="w-full h-48 object-cover" data-testid={`asset-image-${a.id}`} />
                )}
                <div className="p-3 space-y-1">
                  <div className="flex items-center justify-between"><span className="text-xs text-white truncate">{a.name}</span><StatusPill status={a.approval_state} /></div>
                  <p className="text-[10px] text-[#64748B]">{t("md.assetBy", { a: a.created_by_agent, m: a.source_model })}</p>
                  <p className="text-[10px] text-[#475569]">{t("md.assetToolLine", { t: a.source_tool, p: a.source_provider, c: a.cost })}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-[#64748B] mt-3">{t("md.draftNote")}</p>
        </Section>
      )}

      {/* SPEC-06: Tool Runs (governed execution audit) */}
      {(m.tool_runs || []).length > 0 && (
        <Section icon={Wrench} title={t("md.toolRuns")} testid="mission-tool-runs" accent="#A78BFA">
          <div className="space-y-2">
            {m.tool_runs.map((tr) => (
              <div key={tr.id} className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3 flex items-center justify-between gap-3 text-xs" data-testid={`tool-run-${tr.id}`}>
                <div className="min-w-0">
                  <p className="text-white">{(tr.requested_capabilities || []).join(", ")} · <span className="text-[#94A3B8]">{tr.model || "—"}</span></p>
                  <p className="text-[10px] text-[#64748B]">{t("md.toolRunLine", { a: tr.agent_code, c: tr.cost || 0 })}{tr.error_code ? ` · ${tr.error_code}` : ""}</p>
                </div>
                <StatusPill status={tr.status} />
              </div>
            ))}
          </div>
        </Section>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Team */}
        <Section icon={Users} title={t("md.team")} testid="mission-team">
          <div className="space-y-2">
            {m.team?.map((tm) => (
              <div key={tm.id} className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <span className="text-white text-sm font-medium">{tm.agent_name} <span className="text-[#64748B] font-normal">· {tm.position}</span></span>
                  <span className="m-pill" style={{ color: ROLE_COLOR[tm.team_role], borderColor: ROLE_COLOR[tm.team_role], background: "transparent" }}>{tm.team_role} · {tm.selection_score}</span>
                </div>
                <p className="text-[11px] text-[#64748B] mt-1.5">{tm.selection_reason}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Task execution + outputs */}
        <Section icon={ListChecks} title={t("md.tasks")} testid="mission-tasks">
          <div className="space-y-2">
            {compileTask && (
              <details className="bg-[#0B0F19] border rounded-xl p-3" style={{ borderColor: "#34D399" }} open>
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="text-sm text-[#34D399] font-medium">{compileTask.title} <span className="text-[10px] text-[#64748B] font-mono">· {compileTask.assigned_agent_name}</span></span>
                  <StatusPill status={compileTask.status} />
                </summary>
                <p className="text-xs text-[#94A3B8] mt-2 whitespace-pre-wrap max-h-56 overflow-y-auto">{compileTask.output}</p>
                <TaskMeta task={compileTask} />
              </details>
            )}
            {agentTasks.map((tk) => (
              <details key={tk.id} className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3" data-testid={`task-${tk.id}`}>
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="text-sm text-[#E2E8F0]">{tk.title} <span className="text-[10px] text-[#64748B] font-mono">· {tk.assigned_agent_name}</span></span>
                  <StatusPill status={tk.status} />
                </summary>
                <p className="text-xs text-[#94A3B8] mt-2 whitespace-pre-wrap max-h-56 overflow-y-auto" data-testid={`task-output-${tk.id}`}>{tk.output}</p>
                <TaskMeta task={tk} />
              </details>
            ))}
          </div>
        </Section>
      </div>

      {/* Review results */}
      <Section icon={UserCheck} title={t("md.reviews")} testid="mission-reviews" accent="#FBBF24">
        <div className="space-y-2">
          {reviews.length === 0 && <p className="text-sm text-[#64748B]">{t("md.noReviews")}</p>}
          {reviews.map((tk) => (
            <div key={tk.id} className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3 flex items-start justify-between gap-3">
              <div className="min-w-0"><p className="text-sm text-[#E2E8F0]">{tk.title}</p><p className="text-xs text-[#94A3B8] mt-1">{tk.output}</p><TaskMeta task={tk} /></div>
              <StatusPill status={tk.status} />
            </div>
          ))}
        </div>
      </Section>

      {/* Final asset / result */}
      {m.asset && (
        <Section icon={FileText} title={t("md.finalAsset")} testid="mission-asset" accent="#38BDF8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-white">{m.asset.name}</span>
            <div className="flex items-center gap-2"><span className="mono-label text-[9px]">v{m.asset.current_version} · {m.asset.asset_type}</span><StatusPill status={m.asset.approval_state} /></div>
          </div>
          <p className="text-xs text-[#94A3B8] whitespace-pre-wrap max-h-72 overflow-y-auto">{m.asset.versions?.[0]?.body}</p>
        </Section>
      )}

      {/* Cost & audit */}
      <Section icon={GitCommitHorizontal} title={t("md.costAudit")} testid="mission-audit">
        <p className="text-sm text-[#CBD5E1] mb-3">{t("md.totalCost")} <span className="font-mono text-white">${m.cost}</span></p>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {m.transitions?.filter((tr) => tr.entity_type === "MISSION").map((tr, i) => (
            <div key={tr.id} className="flex items-center gap-2">
              {i > 0 && <span className="text-[#334155]">→</span>}
              <StatusPill status={tr.to_state} />
            </div>
          ))}
        </div>
        <div className="space-y-1 max-h-52 overflow-y-auto">
          {m.activity?.map((a) => (
            <div key={a.id} className="text-[11px] text-[#64748B] flex gap-2"><span className="text-[#475569] font-mono shrink-0">{(a.created_at || "").slice(11, 19)}</span><span className="text-[#94A3B8]">{a.actor}</span>{a.detail}</div>
          ))}
        </div>
      </Section>
    </div>
  );
}
